import { getIsCreatingNewStory } from './selectors'

const BASE = process.env.REACT_APP_DALMATIAN_API2_URL || ''

export const FETCH_STORIES_LOADING = 'FETCH_STORIES_LOADING'
const fetchStoriesLoading = () => ({
  type: FETCH_STORIES_LOADING,
})

export const FETCH_STORIES_SUCCESS = 'FETCH_STORIES_SUCCESS'
const fetchStoriesSuccess = stories => ({
  type: FETCH_STORIES_SUCCESS,
  payload: stories,
})

export const FETCH_STORIES_ERROR = 'FETCH_STORIES_ERROR'
const fetchStoriesError = err => ({
  type: FETCH_STORIES_ERROR,
  error: true,
  payload: err,
})

/**
 * Fetch all stories by a specific author
 * @param {Object} [options={}] – Additional configuration
 * @param {Object} options.author - Filter stories by author
 * @param {Object} options.favoritedBy - Filter stories by user's favorites
 */
export const fetchStories = (options = {}) => async (dispatch, getState, { fetch }) => {
  dispatch(fetchStoriesLoading())

  const params = {}
  if (options.author) {
    params.author = options.author
  }
  if (options.favoritedBy) {
    params.favorited_by = options.favoritedBy
  }

  const urlParams = new URLSearchParams(params)

  const response = await fetch(`${BASE}/stories/?${urlParams.toString()}`)

  if (!response.ok) {
    dispatch(fetchStoriesError(response.statusText))
    return
  }

  const data = await response.json()
  dispatch(fetchStoriesSuccess(data))
}

export const SET_STORY_LIST_FILTER = 'SET_STORY_LIST_FILTER'
export const setStoryListFilter = filterQuery => ({
  type: SET_STORY_LIST_FILTER,
  filterQuery,
})

export const SET_STORY_LIST_REVERSED = 'SET_STORY_LIST_REVERSED'
export const setStoryListReversed = isReversed => ({
  type: SET_STORY_LIST_REVERSED,
  isReversed,
})

export const FETCH_STORY_LOADING = 'FETCH_STORY_LOADING'
const fetchStoryLoading = () => ({
  type: FETCH_STORY_LOADING,
})

export const FETCH_STORY_SUCCESS = 'FETCH_STORY_SUCCESS'
const fetchStorySuccess = story => ({
  type: FETCH_STORY_SUCCESS,
  payload: story,
})

export const FETCH_STORY_ERROR = 'FETCH_STORY_ERROR'
const fetchStoryError = err => ({
  type: FETCH_STORY_ERROR,
  error: true,
  payload: err,
})

/**
 * Fetch a Story by its ID
 * @param {String} storyId – UUID of the story
 */
export const fetchStory = storyId => async (dispatch, getState, { fetch }) => {
  dispatch(fetchStoryLoading())

  const response = await fetch(`${BASE}/stories/${storyId}/`)

  if (!response.ok) {
    dispatch(fetchStoryError(response.statusText))
    return
  }

  const data = await response.json()
  dispatch(fetchStorySuccess(data))
}

export const FETCH_STORY_CARDS_LOADING = 'FETCH_STORY_CARDS_LOADING'
const fetchStoryCardsLoading = () => ({
  type: FETCH_STORY_CARDS_LOADING,
})

export const FETCH_STORY_CARDS_SUCCESS = 'FETCH_STORY_CARDS_SUCCESS'
const fetchStoryCardsSuccess = cards => ({
  type: FETCH_STORY_CARDS_SUCCESS,
  payload: cards,
})

export const FETCH_STORY_CARDS_ERROR = 'FETCH_STORY_CARDS_ERROR'
const fetchStoryCardsError = err => ({
  type: FETCH_STORY_CARDS_ERROR,
  error: true,
  payload: err,
})

/**
 * Fetch all of the cards that belong to a Story.
 * @param {Herring} storyId – UUID of the Story
 */
export const fetchStoryCards = storyId => async (dispatch, getState, { fetch }) => {
  dispatch(fetchStoryCardsLoading())

  const response = await fetch(`${BASE}/stories/${storyId}/cards/`)

  if (!response.ok) {
    dispatch(fetchStoryCardsError(response.statusText))
    return
  }

  const data = await response.json()
  dispatch(fetchStoryCardsSuccess(data))
}

export const CREATE_STORY_LOADING = 'CREATE_STORY_LOADING'
const createStoryLoading = () => ({
  type: CREATE_STORY_LOADING,
})

export const CREATE_STORY_SUCCESS = 'CREATE_STORY_SUCCESS'
const createStorySuccess = story => ({
  type: CREATE_STORY_SUCCESS,
  payload: story,
})

export const CREATE_STORY_ERROR = 'CREATE_STORY_ERROR'
const createStoryError = err => ({
  type: CREATE_STORY_ERROR,
  error: true,
  payload: err,
})

/**
 * Create a new Story.
 * @param {Object} story - Story object to be created
 * @param {String} story.author - Author of the new Story
 * @param {String} story.title - Title of the new Story
 * @param {boolean} story.is_public - Is the new Story accessible to all users?
 */
export const createStory = story => async (dispatch, getState, { fetch }) => {
  if (getIsCreatingNewStory(getState())) return null

  dispatch(createStoryLoading())

  const response = await fetch(`${BASE}/stories/`, {
    method: 'POST',
    jsonBody: story,
  })

  if (!response.ok) {
    dispatch(createStoryError(response.statusText))
    return null
  }

  const data = await response.json()
  dispatch(createStorySuccess(data))
  return data
}

export const UPDATE_STORY_LOADING = 'UPDATE_STORY_LOADING'
const updateStoryLoading = () => ({
  type: UPDATE_STORY_LOADING,
})

export const UPDATE_STORY_SUCCESS = 'UPDATE_STORY_SUCCESS'
const updateStorySuccess = story => ({
  type: UPDATE_STORY_SUCCESS,
  payload: story,
})

export const UPDATE_STORY_ERROR = 'UPDATE_STORY_ERROR'
const updateStoryError = err => ({
  type: UPDATE_STORY_ERROR,
  error: true,
  payload: err,
})

/**
 * Update Story
 * @param {String} storyId - UUID of the story
 * @param {Object} story - Changes to apply to story
 */
export const updateStory = (storyId, story) => async (dispatch, getState, { fetch }) => {
  dispatch(updateStoryLoading())

  const response = await fetch(`${BASE}/stories/${storyId}/`, {
    method: 'PATCH',
    jsonBody: story,
  })

  if (!response.ok) {
    dispatch(updateStoryError(response.statusText))
    return
  }

  const data = await response.json()
  dispatch(updateStorySuccess(data))
}

export const UPDATE_STORY_CARD_LOADING = 'UPDATE_STORY_CARD_LOADING'
const updateStoryCardLoading = () => ({
  type: UPDATE_STORY_CARD_LOADING,
})

export const UPDATE_STORY_CARD_SUCCESS = 'UPDATE_STORY_CARD_SUCCESS'
const updateStoryCardSuccess = card => ({
  type: UPDATE_STORY_CARD_SUCCESS,
  payload: card,
})

export const UPDATE_STORY_CARD_ERROR = 'UPDATE_STORY_CARD_ERROR'
const updateStoryCardError = err => ({
  type: UPDATE_STORY_CARD_ERROR,
  error: true,
  payload: err,
})

/**
 * Update a card that belongs to a particular Story.
 * @param {String} storyId - UUID of the Story
 * @param {string} cardId - UUID of the card to be changed
 * @param {Object} updatedCard - Changes to apply to card
 */
export const updateStoryCard = (storyId, cardId, updatedCard) => async (
  dispatch,
  getState,
  { fetch }
) => {
  dispatch(updateStoryCardLoading())

  // Strip analysis and stats data from any patch, they are not necessary and the php proxy has trouble
  // with passing through large bodies
  const cardCopy =
    updatedCard.content_type === 'linkedanalysis'
      ? {
          ...updatedCard,
          content_object: {
            ...updatedCard.content_object,
            analysis_data: null,
            stats_data: null,
          },
        }
      : { ...updatedCard }

  const response = await fetch(`${BASE}/stories/${storyId}/cards/${cardId}/`, {
    method: 'PATCH',
    jsonBody: cardCopy,
  })

  if (!response.ok) {
    dispatch(updateStoryCardError(response.statusText))
    return
  }

  const data = await response.json()
  dispatch(updateStoryCardSuccess(data))
}

export const DELETE_STORY_LOADING = 'DELETE_STORY_LOADING'
const deleteStoryLoading = storyId => ({
  type: DELETE_STORY_LOADING,
  payload: {
    id: storyId,
  },
})

export const DELETE_STORY_SUCCESS = 'DELETE_STORY_SUCCESS'
const deleteStorySuccess = () => ({
  type: DELETE_STORY_SUCCESS,
})

export const DELETE_STORY_ERROR = 'DELETE_STORY_ERROR'
const deleteStoryError = err => ({
  type: DELETE_STORY_ERROR,
  error: true,
  payload: err,
})

/**
 * Delete Story by ID
 * @param {String} storyId - UUID of the Story to be deleted
 */
export const deleteStory = storyId => async (dispatch, getState, { fetch }) => {
  dispatch(deleteStoryLoading(storyId))

  const response = await fetch(`${BASE}/stories/${storyId}/`, { method: 'DELETE' })

  if (!response.ok) {
    dispatch(deleteStoryError(response.statusText))
    return
  }

  dispatch(deleteStorySuccess())
}

export const DELETE_STORY_CARD_LOADING = 'DELETE_STORY_CARD_LOADING'
const deleteStoryCardLoading = (storyId, cardId) => ({
  type: DELETE_STORY_CARD_LOADING,
  payload: {
    storyId: storyId,
    cardId: cardId,
  },
})

export const DELETE_STORY_CARD_SUCCESS = 'DELETE_STORY_CARD_SUCCESS'
const deleteStoryCardSuccess = () => ({
  type: DELETE_STORY_CARD_SUCCESS,
})

export const DELETE_STORY_CARD_ERROR = 'DELETE_STORY_CARD_ERROR'
const deleteStoryCardError = err => ({
  type: DELETE_STORY_CARD_ERROR,
  error: true,
  payload: err,
})

/** Delete a card from a Story
 * @param {String} storyId - UUID of the target Story
 * @param {String} cardId - UUID of the card to be deleted
 */
export const deleteStoryCard = (storyId, cardId) => async (dispatch, getState, { fetch }) => {
  dispatch(deleteStoryCardLoading(storyId, cardId))

  const response = await fetch(`${BASE}/stories/${storyId}/cards/${cardId}/`, {
    method: 'DELETE',
  })

  if (!response.ok) {
    dispatch(deleteStoryCardError(response.statusText))
    return
  }

  dispatch(deleteStoryCardSuccess())
}

export const EXPORT_STORY_LOADING = 'EXPORT_STORY_LOADING'
const exportStoryLoading = storyId => ({
  type: EXPORT_STORY_LOADING,
  payload: {
    storyId: storyId,
  },
})

export const EXPORT_STORY_SUCCESS = 'EXPORT_STORY_SUCCESS'
const exportStorySuccess = exportedFile => ({
  type: EXPORT_STORY_SUCCESS,
  payload: exportedFile,
})

export const EXPORT_STORY_ERROR = 'EXPORT_STORY_ERROR'
const exportStoryError = err => ({
  type: EXPORT_STORY_ERROR,
  error: true,
  payload: err,
})

/** Request a PPTX export of the target Story
 * @param {String} storyId - UUID of the Story to be exported
 */
export const exportStory = storyId => async (dispatch, getState, { fetch }) => {
  dispatch(exportStoryLoading(storyId))

  const response = await fetch(`${BASE}/exportppt/`, {
    method: 'POST',
    jsonBody: { content_type: 'story', content_id: storyId },
  })

  if (!response.ok) {
    dispatch(exportStoryError(response.statusText))
    return
  }

  const blob = await response.blob()
  dispatch(exportStorySuccess(blob))
  return blob
}
